<template>
    <div>
        <modal
            :append-to-body="true"
            v-bind="attrs"
            v-on="$listeners"
            @close="onCloseDialog"
            :width="modalWidth"
            id="componentStep"
            top="50px"
            titleCentered
        >
            <template v-slot:title>
                <span class="form-title main-text-color">
                    {{ $t('widget.widgetReorder') }}
                </span>
            </template>
            <fade-transition
                :duration="transitionDuration"
                mode="out-in"
            >
                <tab-card
                    :data="dataReportExport"
                    :active-tab.sync="activeTab"
                    class="border-t md:border-0 mt-7 px-10"
                    data-test-name="report-item--edit"
                    :white-bg="true"
                >
                    <template v-for="(dynamicTab, indexCol) in dataReportExport" :slot="dynamicTab.name">
                        <draggable-list
                            v-if="dynamicTab.reportItemExport.length"
                            :value="widgets"
                            @change="(ev) => onGroupListChange(ev, dynamicTab.reportItemExport)"
                            group="widgets"
                            :key="indexCol"
                        >
                            <div
                                v-for="(widgetItem, indexRow) in dynamicTab.reportItemExport"
                                :key="indexRow"
                                class="w-full flex justify-between items-center items py-4 px-8"
                                :class="{ 'border-b': (indexRow + 1) !== dynamicTab.reportItemExport.length }"
                            >
                                <div class="widget-item flex justify-center">
                                    <span class="main-text-color font-bold text-xl min-w-5 flex items-center justify-center number-row">
                                        {{ indexRow + 1 }}
                                    </span>
                                    <div class="flex flex-col">
                                        <span class="main-text-color text-xl">
                                            {{ widgetItem.WidgetTitle }}
                                        </span>
                                        <span v-if="checkIfExistReportItemDescription(widgetItem.ReportItemDescription)">
                                            {{ widgetItem.ReportItemDescription }}
                                        </span>
                                    </div>
                                </div>
                                <div class="flex cursor-pointer items-center justify-center">
                                    <i class="vc-icon-recycle-bin text-xl text-red mx-6" @click="deleteWidget(dynamicTab.reportItemExport, indexCol, indexRow)" />
                                    <icon-drag-and-drop v-if="dynamicTab.reportItemExport.length > 1" class="text-gray-500 hover:text-primary"/>
                                </div>
                            </div>
                        </draggable-list>
                        <div v-else :key="`empty-data-${indexCol}`" class="no-data-block"> 
                            <h3 class="text-main-xl">{{ $t('general.noData') }}</h3>
                        </div>
                    </template>
                </tab-card>
            </fade-transition>
            <template v-slot:footer>
                <div class="border-t-2 border-gray-300 py-4 px-10 flex items-center justify-end main-bg-light">
                    <el-button
                        @click="submitWidgets"
                        class="button-set-up-widgets font-bold"
                        type="primary"
                    >
                        {{ $t('general.add') }}
                    </el-button>
                </div>
            </template>
        </modal>
        <ConfirmDialog
            :visible.sync="showConfirmDialog"
            top="18vh"
            @on-close="onCancel"
        >
            <template v-slot:title>
                <h3 class="text-main-2xl font-semibold text-gray-700">
                    {{ $t('common.save.changes') }}
                </h3>
            </template>
            <div class="mt-10 mb-8 flex flex-col items-center justify-center">
                <IconQuestion />
                <div class="text-center main-text-color text-sm leading-21 mt-5 max-w-65-p break-normal">
                    {{ $t('report.doYouWantSaveChanges') }}
                </div>
            </div>
            <template v-slot:footer-actions>
                <slot name="footer-actions">
                    <base-button
                        outline
                        fixed-width="w-32"
                        @click="onCancel">
                        <div class="flex items-center">
                            <IconDiscard class="mx-1"/>
                            <span class="mx-1 text-base font-bold">{{ $t('common.cancel') }}</span>
                        </div>
                    </base-button>
                    <base-button
                        type="primary"
                        @click="onConfirm"
                        fixed-width="w-32"
                        key="store"
                    >
                        <span class="mx-1 text-base font-bold">{{ $t('common.confirm') }}</span>
                    </base-button>
                </slot>
            </template>
        </ConfirmDialog>
        <delete-dialog
            v-if="showDeleteDialog"
            :visible.sync="showDeleteDialog"
            :config="{descriptionIcon: ''}"
            :description="$t('report.deleteReportTriggerConfirmation')"
            @on-close="onCancelDelete"
            @on-cancel="onCancelDelete"
            @on-confirm="deleteReportExportItem"
        />
    </div>
</template>

<script>
import Modal from '@/components/Common/Modal'
import ConfirmDialog from '@/components/Common/ConfirmDialog'
import draggableEvents from '@/enum/draggableEvents'
import { reportApi } from '@/modules/reports/services/reportService'
import { Notification } from 'element-ui'
import DeleteDialog from "@/components/Dialogs/DeleteDialog";
import {
    ReportExportXLSX
} from '@/enum/reportStatuses'

export default {
    components: {
        Modal,
        ConfirmDialog,
        TabCard: () => import('@/modules/reports/components/TabCard.vue'),
        DraggableList: () => import('@/components/Widgets/DraggableList'),
        DeleteDialog
    },
    props: {
        modalWidth: {
            type: String,
            default: '1210px'
        },
        widgets: {
            type: Array,
            default: () => []
        },
        transitionDuration: 100,
        visible: {
            type: Boolean,
            default: false
        },
        reportId: {
            type: [Number, String],
            default: ''
        },
        widgets: {
            type: Array,
            default: () => []
        },
        reportItemList: {
            type: Array,
            default: () => []
        }
    },
    data () {
        return {
            showConfirmDialog: false,
            dataReportExport: [],
            activeTab: null,
            showDeleteDialog: false,
            activeRowData: null
        }
    },
    computed: {
        attrs() {
            return {
                ...this.$attrs,
                visible: this.visible
            }
        },
        confData () {
            return this.$store.getters['reportTrigger/getConfData']
        }
    },
    watch: {
        visible (val) {
            if (val) {
                this.makeReorderWidgetData()
                this.setActiveTab()
            }
        }
    },
    methods: {
        onCloseDialog() {
            this.$emit('update:visible', false)
        },
        async onConfirm () {
            const reportItemExport = this.dataReportExport
                .filter(el => el.reportItemExport.length)
                .map(el => {
                    return el.reportItemExport
                })[0]
                .map(el => {
                    return el.ReportItemExport
                })
                .flat()
            const payload = {
                ReportItemExportList: [
                    ...reportItemExport
                ]
            }

            await reportApi.reportItemExportReorder(payload)
            this.showConfirmDialog = false
            this.$emit('update:visible', false)
            this.$emit('reordered-widgets', this.dataReportExport)
            Notification.success(this.$t('general.notification.success'))
        },
        onCancel () {
            this.showConfirmDialog = false
        },
        submitWidgets () {
            this.showConfirmDialog = true
        },
        onGroupListChange (ev, reportItemExport) {
            let eventData = ev[draggableEvents.MOVED]
            let { newIndex: newIndex, oldIndex: oldIndex } = eventData
            reportItemExport.splice(newIndex, 0, reportItemExport.splice(oldIndex, 1)[0])
            reportItemExport.map((el, index) => {
                el[0].ReportExportOrder = index + 1
                return el
            })
        },
        deleteWidget (tab, indexCol, indexRow) {
            this.activeRowData = {
                tab,
                indexCol,
                indexRow
            }
            this.showDeleteDialog = true
        },
        makeReorderWidgetData () {
            const data = []
            this.confData.ReportExportTypeList.forEach(el => {
                data.push({
                    reportItemExport: [],
                    ...el,
                    title: this.$t(el.ReportExportTypeNameContentTag),
                    name: String(el.ReportExportTypeName).toLowerCase().replaceAll(' ', '_'),
                    icon: el.ReportExportTypeID === ReportExportXLSX ? 'vc-icon-excel' : 'vc-icon-mail',
                    disabled: false,
                    widgetData: null
                })

                this.widgets.forEach(widgetItem => {
                    const reportItemExport = widgetItem.ReportItemExport.filter(item => +item.ReportExportTypeID === +el.ReportExportTypeID)
                    if (reportItemExport.length) {
                        const sameTab = data.find(dataItem => +dataItem.ReportExportTypeID === +el.ReportExportTypeID)
                        sameTab.widgetData = widgetItem
                        sameTab.reportItemExport.push({
                            ...widgetItem,
                            ...reportItemExport
                        })
                    }
                })
            })

            data
                .map(el => {
                    el.disabled = !el.reportItemExport.length
                    el.reportItemExport = el.reportItemExport
                        .sort((a, b) => Number(a[0].ReportExportOrder) - Number(b[0].ReportExportOrder))
                    return el
                })

            this.dataReportExport = data
        },
        setActiveTab () {
            if (this.dataReportExport && this.dataReportExport.length) {
                const allNotEmptyDataInTab = this.dataReportExport.filter(el => el.reportItemExport.length)
                if (allNotEmptyDataInTab && allNotEmptyDataInTab[0] && allNotEmptyDataInTab[0].name) {
                    this.activeTab = allNotEmptyDataInTab[0].name
                }
            }  
        },
        checkIfExistReportItemDescription (description) {
            return description && description !== '{}' ? description : ''
        },
        async deleteReportExportItem () {
            const itemToDelete = this.dataReportExport[this.activeRowData.indexCol].reportItemExport[this.activeRowData.indexRow]
            this.dataReportExport[this.activeRowData.indexCol].reportItemExport.splice(this.activeRowData.indexRow, 1)
            this.onCancelDelete()

            if (!this.dataReportExport[this.activeRowData.indexCol].reportItemExport.length) {
                this.setActiveTab()
            }

            this.dataReportExport = this.dataReportExport
                .map(el => {
                    el.disabled = !el.reportItemExport.length
                    return el
                })
            const dataToSend = {}
            const widgetData = this.dataReportExport[this.activeRowData.indexCol].widgetData
            const keysToSend = ['ReportID', 'WidgetID', 'ReportItemID', 'ReportItemName', 'ReportItemOrder', 'ReportItemStatusID', 'ReportItemDescription']
            keysToSend.forEach(el => {
                dataToSend[el] = widgetData[el]
            })
            const modifiedReportItemList = this.reportItemList
                .filter(el => el.WidgetID === itemToDelete.WidgetID && el.ReportItemID === itemToDelete.ReportItemID)
                .map(el => {
                    el.ReportItemExport = el.ReportItemExport.filter(el => el.ReportExportTypeID !== itemToDelete[0].ReportExportTypeID)
                    return el
                })

            dataToSend.ReportItemExport = modifiedReportItemList[0].ReportItemExport
            const data = {
                ReportItemList: [dataToSend]
            }
            await reportApi.reportItemBulkUpsert(data)
            Notification.success(this.$t('general.notification.success'))
            await reportApi.info(this.reportId)
        },
        onCancelDelete () {
            this.showDeleteDialog = false
        }
    },
    async beforeDestroy () {
        this.showConfirmDialog = false
    }
}
</script>

<style lang="scss" scoped>
.redirect-action {
    @apply text-sm flex items-center px-8 cursor-pointer;
}
.form-title {
    @apply font-bold text-xl;
    color: var(--mainTextColor-color) !important;
}
::v-deep .tab-card-slot {
    max-height: 300px;
    height: 100%;
    overflow-y: auto;
}
.no-data-block {
    height: 300px;
    @apply flex items-center justify-center;
}
[dir="rtl"] .number-row {
    @apply ml-12;
}
[dir="ltr"] .number-row {
    @apply mr-12;
}
</style>
